import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  MapPin,
  Loader2,
  Navigation,
  RefreshCcw,
} from "lucide-react";
import { Button } from "../../../components/ui/button";

// Mappls REST reverse-geocode: legacy endpoint uses the API key in the URL path.
// (The modern "access_token" endpoint returns 401 for this key in this project.)
const MAPPLS_ACCESS_TOKEN = "bb455d19ed3189e52f199c00f82ea641";

function isFiniteNumber(n) {
  return typeof n === "number" && Number.isFinite(n);
}

function parseMaybeNumber(s) {
  const n = typeof s === "string" ? Number(s) : NaN;
  return Number.isFinite(n) ? n : null;
}

async function reverseGeocodeMappls({ lat, lng, signal }) {
  // Legacy endpoint (works with this project's configured key)
  const url = new URL(
    `https://apis.mappls.com/advancedmaps/v1/${encodeURIComponent(
      MAPPLS_ACCESS_TOKEN
    )}/rev_geocode`
  );
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lng", String(lng));
  // Mappls treats India as default, but we keep region if the backend supports it.
  url.searchParams.set("region", "IND");

  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Mappls reverse geocode failed (${res.status}) ${text}`.trim());
  }

  const data = await res.json();
  if (data?.responseCode && Number(data.responseCode) !== 200) {
    const msg = data?.error || data?.error_description || "Reverse geocode returned an error";
    throw new Error(`Mappls reverse geocode failed: ${msg}`.trim());
  }
  const first = Array.isArray(data?.results) ? data.results[0] : null;
  return {
    poi: first?.poi || "",
    formattedAddress: first?.formatted_address || first?.formattedAddress || "",
    street: first?.street || "",
    locality: first?.locality || "",
    subLocality: first?.subLocality || first?.subSubLocality || "",
    city: first?.city || "",
    district: first?.district || "",
    state: first?.state || "",
    pincode: first?.pincode || "",
    lat: first?.lat ?? lat,
    lng: first?.lng ?? lng,
  };
}

async function reverseGeocodeBigDataCloud({ lat, lng, signal }) {
  const url = new URL("https://api.bigdatacloud.net/data/reverse-geocode-client");
  url.searchParams.set("latitude", String(lat));
  url.searchParams.set("longitude", String(lng));
  url.searchParams.set("localityLanguage", "en");

  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`BDC reverse geocode failed (${res.status}) ${text}`.trim());
  }

  const data = await res.json();
  const locality = data?.locality || "";
  const city = data?.city || "";
  const district = data?.localityInfo?.administrative?.[1]?.name || "";
  const state = data?.principalSubdivision || "";
  const country = data?.countryName || "";
  const pincode = data?.postcode || "";

  // Keep fallback human-readable and local (avoid broad hierarchy like "Asia, ...").
  const compactAddress = [locality || city, district, state, country]
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(", ");

  return {
    poi: locality || city || "",
    formattedAddress: compactAddress || [city, state, country].filter(Boolean).join(", "),
    street: locality || city || "N/A",
    locality,
    subLocality: locality || district || "N/A",
    city: city || locality || "N/A",
    district: district || "N/A",
    state,
    pincode: pincode || "N/A",
    lat,
    lng,
  };
}

async function reverseGeocodeNominatim({ lat, lng, signal }) {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lng));
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("zoom", "18");

  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: {
      Accept: "application/json",
      "User-Agent": "trackyourwheelz-frontend/1.0",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Nominatim reverse geocode failed (${res.status}) ${text}`.trim());
  }

  const data = await res.json();
  const addr = data?.address || {};
  const poi =
    data?.name ||
    addr?.attraction ||
    addr?.building ||
    addr?.residential ||
    addr?.amenity ||
    "";
  const city = addr?.city || addr?.town || addr?.village || "";
  const area = addr?.suburb || addr?.neighbourhood || addr?.quarter || "";

  return {
    poi,
    formattedAddress: data?.display_name || [area, city, addr?.state, addr?.country].filter(Boolean).join(", "),
    street: addr?.road || addr?.residential || poi || "N/A",
    locality: area || "N/A",
    subLocality: area || "N/A",
    city: city || "N/A",
    district: addr?.county || addr?.state_district || "N/A",
    state: addr?.state || "N/A",
    pincode: addr?.postcode || "N/A",
    lat,
    lng,
  };
}

export default function LatLngLocationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [retryToken, setRetryToken] = useState(0);

  const latLng = useMemo(() => {
    const lat = parseMaybeNumber(searchParams.get("lat"));
    const lng = parseMaybeNumber(searchParams.get("lng"));
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return null;
    return { lat, lng };
  }, [searchParams]);

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [address, setAddress] = useState(null);

  useEffect(() => {
    if (!latLng) return;

    const controller = new AbortController();
    setStatus("loading");
    setErrorMsg("");
    setAddress(null);

    reverseGeocodeMappls({
      lat: latLng.lat,
      lng: latLng.lng,
      signal: controller.signal,
    })
      .catch(() =>
        reverseGeocodeNominatim({
          lat: latLng.lat,
          lng: latLng.lng,
          signal: controller.signal,
        })
      )
      .catch(() =>
        reverseGeocodeBigDataCloud({
          lat: latLng.lat,
          lng: latLng.lng,
          signal: controller.signal,
        })
      )
      .then((a) => {
        if (controller.signal.aborted) return;
        setAddress(a);
        setStatus("success");
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setAddress({
          poi: "",
          formattedAddress: `Lat ${latLng.lat.toFixed(6)}, Lng ${latLng.lng.toFixed(6)}`,
          street: "N/A",
          locality: "N/A",
          subLocality: "N/A",
          city: "N/A",
          district: "N/A",
          state: "N/A",
          pincode: "N/A",
          lat: latLng.lat,
          lng: latLng.lng,
        });
        setErrorMsg(err?.message || "Failed to get location");
        setStatus("success");
      });

    return () => controller.abort();
  }, [latLng, retryToken]);

  const coordText = useMemo(() => {
    if (!latLng) return "";
    // Match the precision shown in the timeline examples.
    return `${latLng.lat}, ${latLng.lng}`;
  }, [latLng]);

  const copyCoords = useCallback(async () => {
    if (!latLng) return;
    try {
      await navigator.clipboard.writeText(coordText);
    } catch {
      // no-op: clipboard may be blocked
    }
  }, [latLng, coordText]);

  const openInGoogleMaps = useCallback(() => {
    if (!latLng) return;
    const url = `https://www.google.com/maps?q=${encodeURIComponent(`${latLng.lat},${latLng.lng}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [latLng]);

  const title = address?.poi || address?.formattedAddress || "Location";

  const HERO_BG = useMemo(() => {
    // Decorative "map-like" thumbnail (inline SVG -> data URI).
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="720" height="360" viewBox="0 0 720 360">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#0ea5e9" stop-opacity="0.45"/>
            <stop offset="1" stop-color="#6366f1" stop-opacity="0.15"/>
          </linearGradient>
          <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke="#ffffff" stroke-opacity="0.12" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="720" height="360" fill="url(#g)"/>
        <rect width="720" height="360" fill="url(#grid)"/>
        <path d="M20 240 C140 150, 220 300, 340 200 S 520 120, 700 170" fill="none" stroke="#22c55e" stroke-width="6" stroke-opacity="0.22" stroke-linecap="round"/>
        <path d="M60 280 C160 210, 230 330, 360 240 S 520 160, 680 210" fill="none" stroke="#38bdf8" stroke-width="4" stroke-opacity="0.2" stroke-linecap="round"/>
        <circle cx="410" cy="200" r="34" fill="#f97316" fill-opacity="0.16"/>
        <circle cx="410" cy="200" r="10" fill="#f97316" fill-opacity="0.5"/>
      </svg>
    `;
    return `url("data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}")`;
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Image-like hero header */}
      <div
        className="relative h-44 w-full"
        style={{
          backgroundImage: HERO_BG,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/10 to-background" />

        <div className="sticky top-0 z-20 flex h-14 items-center justify-between gap-2 border-b border-white/10 bg-transparent px-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2 text-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

      </div>

      <div className="mx-auto w-full max-w-md p-3 ">
        <div className="rounded-2xl border-1 border-muted-foreground/20 p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-foreground">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="min-w-0 bo">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Coordinates</p>
                <p className="break-words font-mono text-sm text-foreground">{coordText || "—"}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-xl"
              onClick={copyCoords}
              aria-label="Copy coordinates"
            >
              <Copy className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-md p-3 pt-0">
        <div className="rounded-2xl border-1 border-muted-foreground/20 bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Location</p>
            <div className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
              <Navigation className="h-3.5 w-3.5" />
              Reverse Geocode
            </div>
          </div>

          {status === "loading" && (
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Getting address near these coordinates…
              </div>
              <div className="space-y-2">
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted/70" />
                <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted/50" />
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="mt-3 rounded-xl border border-destructive/20 bg-destructive/5 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                <Navigation className="h-4 w-4" />
                Could not fetch location
              </p>
              <p className="mt-1 text-xs text-destructive/80 break-words">{errorMsg || "Unknown error"}</p>
              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={() => setRetryToken((t) => t + 1)}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Try again
                </Button>
              </div>
            </div>
          )}

          {status === "success" && address && (
            <div className="mt-3 space-y-3">
              {address.poi ? (
                <p className="text-base font-semibold text-foreground">{address.poi}</p>
              ) : null}
              {address.formattedAddress ? (
                <p className="text-sm text-foreground leading-relaxed">{address.formattedAddress}</p>
              ) : null}

              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="rounded-xl bg-muted/40 p-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Street</p>
                  <p className="mt-1 text-xs text-foreground break-words">{address.street || "N/A"}</p>
                </div>
                <div className="rounded-xl bg-muted/40 p-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Area</p>
                  <p className="mt-1 text-xs text-foreground break-words">{address.subLocality || "N/A"}</p>
                </div>
                <div className="rounded-xl bg-muted/40 p-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">City</p>
                  <p className="mt-1 text-xs text-foreground break-words">{address.city || "N/A"}</p>
                </div>
                <div className="rounded-xl bg-muted/40 p-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Pincode</p>
                  <p className="mt-1 text-xs text-foreground break-words">{address.pincode || "N/A"}</p>
                </div>
                <div className="rounded-xl bg-muted/40 p-2 col-span-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">State</p>
                  <p className="mt-1 text-xs text-foreground break-words">{address.state || "N/A"}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              className="flex-1 rounded-xl"
              onClick={openInGoogleMaps}
              disabled={!latLng || status === "loading"}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in Maps
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

